/**
 * iframe 承接页 guest token 检查、刷新与高阶函数封装。
 */
import { getLocaleState } from '@home/utils/state';

import type {
  IDatalabTokenMessageBus,
  ITokenData,
  THandleConfig,
  TPromiseHandle,
  TTokenRefreshEventPayload,
} from './types';

/**
 * 读取 iframe 承接页的 messageBus；非 iframe 场景返回 undefined。
 */
const getDatalabTokenMessageBus = (): IDatalabTokenMessageBus | undefined => {
  const messageBus = getLocaleState('datalabMessageBus');
  if (!messageBus || typeof messageBus !== 'object') {
    return undefined;
  }
  return messageBus as IDatalabTokenMessageBus;
};

/**
 * 将 messageBus 上的字段规范为 ITokenData。
 */
const buildTokenDataFromBus = (messageBus: IDatalabTokenMessageBus): ITokenData => ({
  origin: messageBus.origin ?? '',
  token: messageBus.token ?? '',
  gameCode: messageBus.gameCode ?? '',
  dashboardId: messageBus.dashboardId ?? '',
});

/**
 * 判断 token 刷新回调是否表示失败。
 */
const isTokenRefreshFailure = (
  payload: TTokenRefreshEventPayload,
): payload is { code: number } =>
  'code' in payload && typeof payload.code === 'number' && payload.code !== 0;

let refreshTokenLock = false;
let refreshTokenPromise: Promise<ITokenData>;
let refreshTokenCallback: ((result: TTokenRefreshEventPayload) => void) | null =
  null;

/**
 * 在 iframe 承接页执行请求前检查 guest token；过期则向父页刷新后再执行 promiseHandle。
 */
export const checkAndRefreshToken = async <
  TArgs extends readonly unknown[],
  TCallerArgs extends TArgs,
  TResult,
>(
  promiseHandle: TPromiseHandle<TArgs, TResult>,
  handleConfig: THandleConfig<TArgs, TCallerArgs>,
  ...args: TCallerArgs
): Promise<TResult> => {
  const datalabMessageBus = getDatalabTokenMessageBus();

  if (!datalabMessageBus) {
    return promiseHandle(...args);
  }

  if (
    datalabMessageBus.token &&
    datalabMessageBus.checkTokenExpiry(datalabMessageBus.token)
  ) {
    const tokenData = buildTokenDataFromBus(datalabMessageBus);
    const newArgs = handleConfig(tokenData, ...args);
    return promiseHandle(...newArgs);
  }

  if (!refreshTokenLock) {
    refreshTokenLock = true;
    refreshTokenPromise = new Promise<ITokenData>((resolve, reject) => {
      refreshTokenCallback = (result: TTokenRefreshEventPayload) => {
        if (isTokenRefreshFailure(result)) {
          reject(result);
          return;
        }
        resolve(result);
      };

      datalabMessageBus.on(
        datalabMessageBus.updatedTokenEventType,
        refreshTokenCallback,
      );
      datalabMessageBus.notifyRefreshToken();
    })
      .catch((error: unknown) => {
        console.log('获取Token报错', error);
        return Promise.reject(error);
      })
      .finally(() => {
        setTimeout(() => {
          refreshTokenLock = false;
          if (refreshTokenCallback) {
            datalabMessageBus.off(
              datalabMessageBus.updatedTokenEventType,
              refreshTokenCallback,
            );
            refreshTokenCallback = null;
          }
        }, 3000);
      });
  }

  try {
    const result = await refreshTokenPromise;
    const newArgs = handleConfig(result, ...args);
    return promiseHandle(...newArgs);
  } catch (error) {
    console.error('刷新Token失败', error);
    throw error;
  }
};

/**
 * 包装任意异步函数：在 iframe 承接页自动注入/刷新 token 后再调用原函数。
 */
export const createTokenAwareFunction = <
  TArgs extends readonly unknown[],
  TCallerArgs extends TArgs,
  TResult,
>(
  promiseHandle: TPromiseHandle<TArgs, TResult>,
  handleConfig: THandleConfig<TArgs, TCallerArgs>,
): ((...args: TCallerArgs) => Promise<TResult>) =>
  (...args: TCallerArgs) =>
    checkAndRefreshToken(promiseHandle, handleConfig, ...args);
